import type { AxiosError } from "axios";
import { useQuery } from "react-query";
import { fetchFormConfig } from "../../../api";
import type { FormConfig } from "../../../types/form";

interface Args {
  accountId: string;
  hash: string;
  isPreview?: boolean;
  passcode?: string | null;
}

interface ReturnType {
  data?: FormConfig;
  isLoading: boolean;
  isFetched: boolean;
  statusCode: number | undefined;
}

export const useFetchFormConfig = (args: Args): ReturnType => {
  const { data, status, isLoading, isFetched, error } = useQuery<FormConfig, AxiosError>(
    ["form", args.passcode],
    async () => await fetchFormConfig(args.accountId, args.hash, args.isPreview, args.passcode),
    {
      refetchOnWindowFocus: true,
      retry: shouldRetry,
      retryDelay: RETRY_DELAY_MS,
    },
  );

  const statusCode = defineStatusCode(isLoading, status, error);

  return { data, isLoading, statusCode, isFetched };
};

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

// A 4xx answer is final (missing form, passcode required, inactive plan), so retrying
// it only keeps the visitor on the preloader instead of the matching screen.
const shouldRetry = (failureCount: number, error: AxiosError): boolean => {
  const status = error.status ?? 0;

  if (status >= 400 && status < 500) {
    return false;
  }

  return failureCount < MAX_RETRIES;
};

const defineStatusCode = (
  isLoading: boolean,
  status: string,
  error?: AxiosError | null,
): number | undefined => {
  if (isLoading) {
    return 0; // Loading state
  }

  if (status === "success") {
    return 200;
  }

  if (error?.status) {
    return error.status;
  }
};
